<template>
  <b-modal ref="modal" visible size="lg" :hide-footer="true" title="Bestelling details" @hidden="$emit('close')">
    <div v-if="order === null" class="text-center p-50">
      <i class="fa fa-spin fa-5x fa-spinner"></i>
    </div>

    <div v-else>
      <div v-if="order.customer_note" class="alert alert-warning" role="alert">
        <h4 class="alert-heading font-w400 my-2">Notitie van klant:</h4>
        <p class="mb-2">{{order.customer_note}}</p>
      </div>

      <div v-if="order.gift_wrapping" class="alert alert-warning" role="alert">
        <p class="mb-0"><i class="fa fa-info-circle mr-5"></i> Deze bestelling moet WEL ingepakt worden.</p>
      </div>
      <div v-else class="alert alert-info" role="alert">
        <p class="mb-0"><i class="fa fa-info-circle mr-5"></i> Deze bestelling hoeft NIET ingepakt te worden.</p>
      </div>
      <div v-for="orderNotification in orderNotifications" :key="orderNotification.id" class="alert alert-info" role="alert">
        <p class="mb-0"><i class="fa fa-info-circle mr-5"></i> {{orderNotification.notification_text}}</p>
      </div>
      <div v-if="order.discount_code" class="alert alert-info" role="alert">
        <h4 class="alert-heading font-w400 my-2">Kortingscode:</h4>
        <p class="mb-2">{{order.discount_code}}</p>
      </div>

      <div class="block block-bordered">
        <div class="block-header block-header-default">
          <h3 class="block-title">
            Bestelling #{{order.number}}
            <order-status-badge :order="order" class="ml-2" ref="orderStatus" />
          </h3>

          <div class="block-options">
            <small class="mr-1">Download:</small>
            <a :href="invoicePdfUrl" target="_blank" class="btn btn-sm btn-secondary"><i class="fa fa-file-pdf-o text-danger mr-1"></i> Pakbon</a>
            <a v-if="order.shipment" :href="shipmentPdfUrl" target="_blank" class="btn btn-sm btn-secondary"><i class="fa fa-tag text-warning mr-1"></i> Verzendlabel</a>
          </div>

          <div class="block-options">
            <small class="mr-1"></small>
            <a :href="invoiceOrderPdfUrl" target="_blank" class="btn btn-sm btn-secondary"><i class="fa fa-file-pdf-o text-danger mr-1"></i> Factuur</a>
          </div>

        </div>
        <order-timeline :order="order" @print:label="printLabel"></order-timeline>
      </div>
      <div v-if="order.shipping_method === 'pickup'" class="block block-bordered">
        <div class="block-content block-content-full">
          <div class="py-20 text-center">
            <div class="font-size-h4 font-w600">Wordt opgehaald</div>
            <div class="text-muted">Deze bestelling wordt opgehaald door de klant.</div>
            <div class="pt-20" v-if="order.status !== 'completed'">
              <button class="btn btn-rounded btn-alt-success" @click="completeOrder">
                <i class="fa fa-check mr-5"></i> Bestelling voltooien
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="!order.shipment" class="block block-bordered">
        <div class="block-header block-header-default">
          <h3 class="block-title">Verzending aanmaken</h3>
        </div>
        <div class="block-content">
          <div class="row gutters-tiny">
            <div class="col-md-4">
              <package-type icon="dropbox" :selected="shipment.package_type === 1" :provider="shipmentOptions[1]" @click="selectPackageType(1)">
                Groot Pakket
              </package-type>
            </div>

            <div class="col-md-4">
              <package-type icon="folder-open" :selected="shipment.package_type === 2" :provider="shipmentOptions[2]" @click="selectPackageType(2)">
                Brievenbuspakket
              </package-type>
            </div>

            <div class="col-md-4">
              <package-type icon="envelope-open" :selected="shipment.package_type === 3" :provider="shipmentOptions[3]" @click="selectPackageType(3)">
                Envelop
              </package-type>
            </div>
            
            <div class="col-md-4">
              <package-type icon="dropbox" :selected="shipment.package_type === 4" :provider="shipmentOptions[4]" @click="selectPackageType(4)">
                Klein Pakket <br>
                <small>max 3 kg, lengte max 50 cm, omtrek max 111 cm</small>
              </package-type>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center my-20">
            <div :title="!showShipmentOptionToggles ? 'Deze opties zijn niet van toepassing op de geselecteerde verzendmethode.' : ''">
              <label class="css-control css-control-primary css-switch" :class="{'disabled': !showShipmentOptionToggles}">
                <input type="checkbox" class="css-control-input" v-model="shipment.insurance" :disabled="!showShipmentOptionToggles">
                <span class="css-control-indicator"></span> Verzekerd
              </label>

              <label class="css-control css-control-primary css-switch" :class="{'disabled': !showShipmentOptionToggles}">
                <input type="checkbox" class="css-control-input" v-model="shipment.large_format" :disabled="!showShipmentOptionToggles">
                <span class="css-control-indicator"></span> XL Pakket
              </label>
            </div>

            <button v-if="shipment.package_type === 3" @click="openPostageStampDetailModal" class="btn btn-hero btn-alt-secondary">
              <i class="fa fa-send mr-5 text-primary"></i>
              Verzend en print label
            </button>
            <button v-else @click="createShipment" class="btn btn-hero btn-alt-secondary" :disabled="creatingShipment">
              <i class="fa fa-spinner fa-spin mr-5" v-if="creatingShipment"></i>
              <i class="fa fa-send mr-5 text-primary" v-else></i>
              Verzend en print label
            </button>
          </div>
        </div>
      </div>

      <order-address :order="order"></order-address>


      <div v-if="order.contains_sol_product && !order.shipping_phone" class="block block-bordered">
        <div class="block-header bg-danger">
          <h3 class="block-title" style="color:white">Telefoonnummer</h3>
        </div>
        <div class="block-content">
          <div class="row justify-content-center py-sm-3 py-md-5">

             <div class="col-sm-10 col-md-8">
                <div class="mb-4">
                  <label class="form-label" for="telephonenumber">Telefoonnummer</label>
                  <input type="text" class="form-control form-control-alt" id="telephonenumber" v-model="customerPhonenumber" name="telephonenumber" placeholder="Vul een telefoon nummer in">
                </div>
                <div class="block-content block-content-full block-content-sm bg-body-light">
                  <button @click="setPhonenumber" type="submit" class="btn btn-alt-primary">
                    <i class="fa fa-check opacity-50 me-1"></i> Vul Villa Madelief nummer in
                  </button>
                </div>
              </div>
          </div>
        </div>
        <div class="block-content block-content-full block-content-sm bg-body-light">
          <button @click="updatePhonenumber" type="submit" class="btn btn-alt-primary">
            <i class="fa fa-check opacity-50 me-1"></i> Submit
          </button>
        </div>
      </div>

      <div class="block block-bordered">
        <div class="block-header block-header-default">
          <h3 class="block-title">Producten</h3>
        </div>
        <div class="block-content p-0">
          <table class="table table-borderless table-striped table-hover">
            <thead>
              <tr>
                <th style="width: 150px;">EAN</th>
                <th>Product</th>
                <th class="text-center">Aantal</th>
                <th>Locatie</th>
                <th>Productnummer Leverancier</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in order.lines" :key="line.id">
                <td>
                  <a class="font-w600" href="#">{{line.product.gtin}}</a>
                </td>
                <td>{{line.product.name}}</td>
                <td class="text-center font-w600">{{line.quantity}}</td>
                <td>
                  <i v-if="line.product.dropship">Wordt nageleverd <small>{{ line.product.dropship }}</small><small v-if="line.dropshipment_completed_at"><i class="fa fa-check fa-4x text-success" style="font-size: 2em"></i></small></i>
                  <span v-else>{{line.product.location}}</span>
                </td>
                <td>{{line.product.vendor_product_id || 'N.v.t.'}}</td>
              </tr>
            </tbody>
            <button v-if="order.contains_sol_product && !order.weshipp_id"  @click="orderSolProducts" type="button" class="btn btn-primary">Bestel Sol producten</button>
          </table>
        </div>
      </div>
    </div>
    <postage-stamp-detail-modal v-if="showPostageStampModal" :order="order" :shipment="shipment"  @close="loadOrder(), showPostageStampModal = false" ></postage-stamp-detail-modal>
  </b-modal>
</template>

<script>
import OrderStatusBadge from './OrderStatusBadge'
import PackageType from './PackageType'
import OrderAddress from './OrderAddress.vue'
import OrderTimeline from './OrderTimeline.vue'
import PostageStampDetailModal from './PostageStampDetailModal.vue'

export default {
  components: {
    OrderStatusBadge,
    PackageType,
    OrderAddress,
    OrderTimeline,
    PostageStampDetailModal
  },
  props: {
    orderId: Number
  },
  data () {
    return {
      order: null,
      shipment: {
        package_type: 1,
        insurance: false,
        large_format: false
      },
      creatingShipment: false,
      customerPhonenumber: null,
      showPostageStampModal: false,
      orderNotifications: []
    }
  },
  computed: {
    showShipmentOptionToggles () {
      return this.shipment.package_type === 1 && this.selectedProvider === 'postnl'
    },
    pickedAt () {
      if (this.order.status === 'picked') {
        return this.order.updated_at
      }

      if (this.order.status === 'completed') {
        return this.completedAt
      }

      return null
    },
    completedAt () {
      if (this.order.status === 'completed') {
        return this.order.shipment ? this.order.shipment.created_at : this.order.updated_at
      }

      return null
    },
    invoicePdfUrl () {
      let url = `${process.env.API_URL}/invoices/${this.order.number}.pdf`
      url += `?access_token=${this.$auth.getAccessToken()}`

      return url
    },
    invoiceOrderPdfUrl () {
      let url = `${process.env.API_URL}/invoices/order/${this.order.number}.pdf?key=${this.order.hash}`

      return url
    },
    shipmentPdfUrl () {
      return `${this.order.shipment.label_url}?access_token=${this.$auth.getAccessToken()}`
    },
    shipmentOptions () {
      return this.order.available_shipment_options || {}
    },
    selectedProvider () {
      return this.shipmentOptions[this.shipment.package_type] || null
    },
    solProductsOrdered () {
      var solProductsOrdered = true
      this.order.lines.forEach(element => {
        if (!element.dropshipment_completed_at) {
          solProductsOrdered = false
        }
      })
      return solProductsOrdered
    }
  },
  methods: {
    loadOrder () {
      const params = {
        include: 'lines,lines.product,shipment,returnShipment'
      }
      this.$api.getOrder(this.orderId, params)
        .then((result) => {
          this.order = result.data.data
          this.orderNotifications = result.data.notifications
        })
        .catch(() => {
          this.$swal('Ehmm...', 'Deze bestelling konden we niet vinden. Probeer het opnieuw.', 'warning')
          this.$refs.modal.hide()
        })
    },
    selectPackageType (type) {
      this.shipment.package_type = type
      this.shipment.insurance = false
      this.shipment.large_format = false
    },
    createShipment () {
      const data = {
        ...this.shipment,
        order_number: this.order.number
      }

      this.creatingShipment = true
      this.$api.createShipment(data)
        .then((result) => this.$api.printLabel(result.data.data.id))
        .then(() => {
          this.creatingShipment = false
          this.loadOrder()
        })
        .catch((error) => {
          this.creatingShipment = false

          if (error.response && error.response.data && error.response.data.message) {
            this.$swal('Oeps! 😳', `Het verzend adres bevat geen huisnummer.`, 'error')
          } else {
            this.$swal('Oeps! 😳', `Er is iets fout gegaan bij het printen van het verzendetiket. Probeer het opnieuw.`, 'error')
          }
        })
    },
    completeOrder () {
      this.$refs.orderStatus.updateStatus('completed')
    },
    orderSolProducts () {
      if (this.order.contains_sol_product && !this.order.shipping_phone) {
        this.$swal('Oeps! 😳', `De producten van sol kunnen niet bestelt worden totdat de bestelling een telefoonnummer heeft. Probeer het opnieuw wanneer een telefoonnummer is ingevoerd.`, 'error')
        return
      }

      this.$http.get(`/orders/solProduct/${this.order.id}`)
        .then((response) => {
          this.$swal('Hoera 😊', `Sol product is succesvol besteld.`, 'success')
        })
        .catch(() => {
          this.$swal(`Er is iets fout gegaan bij het bestellen de sol producten. Probeer het opnieuw.`, 'error')
        })
    },
    printLabel () {
      this.$api.printLabel(this.order.shipment.id)
    },
    setPhonenumber () {
      this.customerPhonenumber = '0857920170'
    },
    updatePhonenumber () {
      if (!this.customerPhonenumber) {
        this.$swal('Oeps! 😳', `Je moet óf het telefoonnummer van de klant opgeven óf het standaard telefoonnummer van Villa Madelief aanvinken.`, 'error')
        return
      }

      const data = {
        shipping_phone: this.customerPhonenumber
      }

      this.$http.patch(`/orders/${this.order.id}`, data)
        .then((response) => {
          this.$swal('Hoera 😊', `Telefoonnummer succesvol geupdate.`, 'success')
        })
        .catch(() => {
          this.$swal(`Er is iets fout gegaan bij het opslaan van de telefoonnummer. Probeer het opnieuw.`, 'error')
        })
    },
    openPostageStampDetailModal () {
      this.showPostageStampModal = true
    }
  },
  created () {
    this.loadOrder()
  }
}
</script>

<style scoped>
a.disabled.block {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>



// WEBPACK FOOTER //
// OrderDetailModal.vue?4d81c5aa